import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { authService, User, RegisterData } from '../services/authService';
import { api } from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ user: User }>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setUserAndTokens: (user: User, tokens: { accessToken: string; refreshToken: string }) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Set up response interceptor for token refresh FIRST, then initialize auth
  useEffect(() => {
    let isMounted = true;

    // Response interceptor - handle token refresh
    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config || {};
        const requestUrl = String(originalRequest?.url || '');
        const isAuthEndpoint =
          requestUrl.includes('/auth/login') ||
          requestUrl.includes('/auth/register') ||
          requestUrl.includes('/auth/verify') ||
          requestUrl.includes('/auth/logout') ||
          requestUrl.includes('/auth/refresh');

        // Never attempt token refresh for auth endpoints themselves.
        // This prevents refresh-on-refresh infinite loops.
        if (error.response?.status === 401 && isAuthEndpoint) {
          return Promise.reject(error);
        }

        // If error is 401 and we haven't already tried to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (!refreshToken) {
              throw new Error('No refresh token available');
            }

            const { accessToken, refreshToken: newRefreshToken } = await authService.refreshToken(refreshToken);
            
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', newRefreshToken);

            // Update user state if component is still mounted
            if (isMounted) {
              try {
                const userData = await authService.getProfile();
                setUser(userData);
              } catch (_err) {
                // If getProfile fails, continue anyway
              }
            }

            // Retry original request with new token
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return api(originalRequest);
          } catch (refreshError) {
            // Refresh failed, logout user
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            if (isMounted) {
              setUser(null);
            }
            // Don't redirect if we're already on login/signup pages
            if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/signup')) {
              window.location.href = '/login';
            }
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    // Initialize auth state from localStorage AFTER interceptor is set up
    const initializeAuth = async () => {
      const token = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (token) {
        try {
          const userData = await authService.getProfile();
          
          // Validate user role - only CUSTOMER role can access frontend
          if (userData.role !== 'CUSTOMER') {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            if (isMounted) {
              setUser(null);
              setLoading(false);
            }
            return;
          }
          
          if (isMounted) {
            setUser(userData);
            setLoading(false);
          }
        } catch (error: any) {
          // Token might be expired, try to refresh
          if (refreshToken) {
            try {
              const { accessToken, refreshToken: newRefreshToken } = await authService.refreshToken(refreshToken);
              localStorage.setItem('accessToken', accessToken);
              localStorage.setItem('refreshToken', newRefreshToken);
              
              // Get user data with new token
              const userData = await authService.getProfile();
              
              // Validate user role - only CUSTOMER role can access frontend
              if (userData.role !== 'CUSTOMER') {
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                if (isMounted) {
                  setUser(null);
                  setLoading(false);
                }
                return;
              }
              
              if (isMounted) {
                setUser(userData);
                setLoading(false);
              }
            } catch (refreshError) {
              // Both token and refresh token are invalid, clear storage
              localStorage.removeItem('accessToken');
              localStorage.removeItem('refreshToken');
              if (isMounted) {
                setUser(null);
                setLoading(false);
              }
            }
          } else {
            // No refresh token, clear storage
            localStorage.removeItem('accessToken');
            if (isMounted) {
              setUser(null);
              setLoading(false);
            }
          }
        }
      } else {
        // No token, user is not logged in
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Initialize auth after interceptor is set up
    initializeAuth();

    return () => {
      isMounted = false;
      api.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { user: userData, tokens } = await authService.login({ email, password });
    
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    
    setUser(userData);
    
    // Return user data so caller can check emailVerified status
    return { user: userData };
  }, []);

  const register = useCallback(async (data: RegisterData) => {
    // Registration doesn't return tokens - user must verify email first
    await authService.register(data);
    // Don't set user or tokens - user must verify email first
    // User will be set after they verify email
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const userData = await authService.getProfile();
      setUser(userData);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, []);

  const setUserAndTokens = useCallback((userData: User, tokens: { accessToken: string; refreshToken: string }) => {
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    setUser(userData);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      login,
      register,
      logout,
      refreshUser,
      setUserAndTokens,
    }),
    [user, loading, login, register, logout, refreshUser, setUserAndTokens]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

